/**
 * Channels resource: TanStack-backed read + mutation hooks. Canonical client
 * interface for channel CRUD.
 *
 * Reads (`useChannels` / `useAllChannels` / `useChannel`) hide the TanStack
 * machinery. `useChannel` derives a single channel from the list cache — there
 * is no `channels.get` procedure. Each `Channel` carries its
 * `memberEmails`, so membership reads come from the same list. Mutations
 * auto-invalidate `channels.list` on success. See `./README.md`.
 */
import type { Channel, ChannelPersona } from '@repo/shared/orpc/procedures/channels';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import type { ApiClient } from 'system/orpc';
import { type Datastore, useDatastore, useMyEmail } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { siloParamsOf } from './silo-params';
import { usePaginatedQuery } from './use-paginated-query';
import { useSiloMutation } from './use-silo-mutation';
import { useSiloQuery } from './use-silo-query';

export type { Channel, ChannelPersona } from '@repo/shared/orpc/procedures/channels';

/**
 * Page size `useAllChannels` requests to pull every channel in one page.
 * Matches the contract's `cursorParams.limit` cap, so a single request returns
 * the whole collection. Stop-gap until real pagination lands in a follow-up.
 */
const ALL_CHANNELS_LIMIT = 100000;

/** Paginated channels. Internal base for `useAllChannels`; export when a
 * pagination-driven consumer needs it. */
function useChannels(params: { limit?: number } = {}) {
    const utils = useApiQueryUtils();
    const result = usePaginatedQuery({ procedure: utils.channels.list, input: { limit: params.limit } });
    const channels = useMemo(() => result.data?.pages.flatMap((page) => page.items), [result.data]);
    return {
        channels,
        isLoading: result.isLoading,
        isFetchingNextPage: result.isFetchingNextPage,
        hasNextPage: result.hasNextPage,
        fetchNextPage: result.fetchNextPage,
    };
}

/**
 * Every channel in the silo, flattened. Thin alias for `useChannels` with a
 * single large page. Bridge for "give me every channel" consumers; new code
 * should prefer `useChannels` with explicit pagination.
 */
export function useAllChannels(): { channels: Channel[]; isLoading: boolean } {
    const { channels, isLoading } = useChannels({ limit: ALL_CHANNELS_LIMIT });
    return { channels: channels ?? [], isLoading };
}

/**
 * One channel, derived from the list cache (there is no `channels.get`), or
 * `undefined` while loading, when disabled (`channelKey` is `null`), or when
 * no channel matches.
 */
export function useChannel(channelKey: string | null): Channel | undefined {
    const { channels } = useAllChannels();
    return useMemo(() => (channelKey ? channels.find((channel) => channel.channelKey === channelKey) : undefined), [channels, channelKey]);
}

/**
 * The keys of the channels the current user belongs to. Backed by
 * `admins.listChannels` for the caller's own email — a self-scoped read every
 * admin role can call. The query stays disabled until the caller's email is
 * known.
 */
export function useMyChannelKeys(): { channelKeys: string[]; isLoading: boolean } {
    const email = useMyEmail();
    const utils = useApiQueryUtils();
    const result = useSiloQuery({ procedure: utils.admins.listChannels, input: { email: email ?? '' }, enabled: !!email });
    return { channelKeys: result.data?.channelKeys ?? [], isLoading: result.isLoading };
}

/**
 * Invalidate the caches affected by a channel write: the silo's `channels.list`
 * (which carries member emails) and the caller's own membership read
 * (`admins.listChannels`, so a change to their own channels shows up).
 */
function invalidateChannelsList(datastore: Datastore) {
    const siloKey = datastore.getSiloKey();
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.channels.list.key({ input: { siloKey } }) }));
    fireAndForget(queryClient.invalidateQueries({ queryKey: utils.admins.listChannels.key({ input: { siloKey } }) }));
}

/** Create a channel. Auto-invalidates the silo's channels list on success. */
export function useCreateChannel() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    return useSiloMutation({
        procedure: utils.channels.create,
        onSuccess: () => invalidateChannelsList(datastore),
    });
}

/** Update a channel. Auto-invalidates the silo's channels list on success. */
export function useUpdateChannel() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    return useSiloMutation({
        procedure: utils.channels.update,
        onSuccess: () => invalidateChannelsList(datastore),
    });
}

/** Delete a channel. Auto-invalidates the silo's channels list on success. */
export function useDeleteChannel() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    return useSiloMutation({
        procedure: utils.channels.delete,
        onSuccess: () => invalidateChannelsList(datastore),
    });
}

/**
 * The channel a space is assigned to, or `null` when unassigned or while
 * loading. Backed by `channels.getSpaceChannel`, which any editorial-dashboard
 * admin can read — even for a channel they cannot access. The query stays
 * disabled until `spaceKey` is known.
 */
export function useSpaceChannel(spaceKey: string | null): { channelKey: string | null; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({
        procedure: utils.channels.getSpaceChannel,
        input: { spaceKey: spaceKey ?? '' },
        enabled: spaceKey !== null,
    });
    return { channelKey: query.data?.channelKey ?? null, isLoading: query.isLoading };
}

/**
 * The channel a space is assigned to, or `null`. Plain async function for
 * callbacks where a hook (`useSpaceChannel`) can't run — e.g. the moderation
 * reply flow resolving a task's channel inside an effect.
 */
export async function getSpaceChannelAsync(datastore: Datastore, { spaceKey }: { spaceKey: string }): Promise<string | null> {
    const { channelKey } = await datastore.getApiClient().channels.getSpaceChannel({ ...siloParamsOf(datastore), spaceKey });
    return channelKey;
}

/**
 * Set (or clear, with `channelKey: null`) the channel a space belongs to.
 * Invalidates the space's `channels.getSpaceChannel` read on success, so any
 * assignment reader refreshes.
 */
export function useAssignChannelToSpace() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    return useSiloMutation({
        procedure: utils.channels.setSpaceChannel,
        onSuccess: (_data, vars) => {
            const queryClient = datastore.getQueryClient();
            fireAndForget(
                queryClient.invalidateQueries({
                    queryKey: utils.channels.getSpaceChannel.key({ input: { siloKey: datastore.getSiloKey(), spaceKey: vars.spaceKey } }),
                }),
            );
        },
    });
}

/**
 * A channel's posting persona (name + photo), resolved via
 * `channels.getPersona` and created on first use. `undefined` while loading or
 * when `channelKey` is unset. Used by the moderation-dashboard reply composer
 * to preview the channel it will post as.
 */
export function useChannelPersona(channelKey: string | undefined): ChannelPersona | undefined {
    const utils = useApiQueryUtils();
    const result = useSiloQuery({ procedure: utils.channels.getPersona, input: { channelKey: channelKey ?? '' }, enabled: !!channelKey });
    return result.data;
}

/** Who a reply is posted as, mirroring the `channels.reply` contract input. */
export type ChannelReplyAs = Parameters<ApiClient['channels']['reply']>[0]['as'];

/**
 * Post a moderation-dashboard reply as a channel or user persona. One server
 * call writes the comment, auto-approves it, and notifies the parent author.
 */
export function usePostChannelReply() {
    const utils = useApiQueryUtils();
    return useSiloMutation({ procedure: utils.channels.reply });
}
